import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Badge } from 'components/ui/badge';
import { Button, buttonVariants } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Switch } from 'components/ui/switch';
import {
  buildMonitoringDeviceRuleDraft,
  buildMonitoringDeviceRuleOverrideInput,
  hasMonitoringDeviceRuleChanges,
  MonitoringDeviceRuleDraft,
} from './ruleHelpers';
import {
  MonitoringDeviceRuleOverrideInput,
  MonitoringDeviceRulesResponse,
  MonitoringRuleParamValue,
} from './types';

function formatValue(value: unknown) {
  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nej';
  }

  return String(value);
}

function formatUpdatedAt(value: Date | null | undefined) {
  return value ? format(value, 'dd/MM/yyyy HH:mm') : 'Aldrig';
}

interface MonitoringDeviceRulesPageContentProps {
  response: MonitoringDeviceRulesResponse;
  isSaving: boolean;
  onSave: (input: MonitoringDeviceRuleOverrideInput) => Promise<unknown>;
}

export default function MonitoringDeviceRulesPageContent({
  response,
  isSaving,
  onSave,
}: MonitoringDeviceRulesPageContentProps) {
  const [draft, setDraft] = useState<MonitoringDeviceRuleDraft>(() =>
    buildMonitoringDeviceRuleDraft(response)
  );

  useEffect(() => {
    setDraft(buildMonitoringDeviceRuleDraft(response));
  }, [response]);

  const isDirty = useMemo(() => hasMonitoringDeviceRuleChanges(draft, response), [draft, response]);

  const updateRule = (
    ruleCode: string,
    updater: (
      rule: MonitoringDeviceRuleDraft['rules'][number]
    ) => MonitoringDeviceRuleDraft['rules'][number]
  ) => {
    setDraft((current) => ({
      ...current,
      rules: current.rules.map((rule) => (rule.ruleCode === ruleCode ? updater(rule) : rule)),
    }));
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-1">
          <div className="text-sm text-muted-foreground">
            <Link className="text-sky-700" to="/monitoring">
              Monitoring
            </Link>{' '}
            /{' '}
            <Link className="text-sky-700" to="/monitoring/rules">
              Regler
            </Link>{' '}
            / {response.sensorTitle}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-3xl font-semibold">{response.sensorTitle}</h1>
            <Badge variant="outline">{response.sensorType}</Badge>
            <Badge variant="outline">{response.deviceFamily}</Badge>
            <Badge variant="outline">{response.isActiveSensor ? 'Aktiv' : 'Inaktiv'}</Badge>
            {response.override && (
              <Badge className="border-amber-200 bg-amber-100 text-amber-900">Har override</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {response.orgTitle} · {response.deviceId} ·{' '}
            <Link className="text-sky-700" to={response.detailPath}>
              Åbn sensor
            </Link>
          </p>
        </div>

        <div className="flex gap-2">
          <Link className={buttonVariants({ variant: 'outline' })} to="/monitoring/rules">
            Tilbage
          </Link>
          <Button
            disabled={!isDirty}
            loading={isSaving}
            onClick={() => onSave(buildMonitoringDeviceRuleOverrideInput(draft, response))}
          >
            Gem override
          </Button>
        </div>
      </div>

      <section className="rounded-md border p-4 text-sm text-muted-foreground">
        <div>
          Standardprofil opdateret {formatUpdatedAt(response.typeProfile.updatedAt)}
          {response.typeProfile.updatedBy ? ` af ${response.typeProfile.updatedBy}` : ''}
        </div>
        <div>
          Seneste override {formatUpdatedAt(response.override?.updatedAt)}
          {response.override?.updatedBy ? ` af ${response.override.updatedBy}` : ''}
        </div>
      </section>

      <section className="space-y-4">
        {response.resolvedRules.map((rule) => {
          const draftRule = draft.rules.find((candidate) => candidate.ruleCode === rule.ruleCode);
          if (!draftRule) {
            return null;
          }

          const editableParams = rule.params.filter(
            (param) => !param.readOnly && param.type !== 'source'
          );
          const readOnlyParams = rule.params.filter(
            (param) => param.readOnly || param.type === 'source'
          );

          return (
            <div key={rule.ruleCode} className="rounded-md border p-4 space-y-4">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-medium">{rule.title}</div>
                    <Badge variant="outline">{rule.ruleCode}</Badge>
                    {rule.feedOnly && <Badge variant="outline">Feed only</Badge>}
                    {rule.isOverridden && (
                      <Badge className="border-amber-200 bg-amber-100 text-amber-900">
                        Override aktiv
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">{rule.description}</div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${rule.ruleCode}-inherit`}
                    checked={draftRule.isInherited}
                    onCheckedChange={(checked) =>
                      updateRule(rule.ruleCode, (currentRule) => ({
                        ...currentRule,
                        isInherited: checked === true,
                        isEnabled:
                          checked === true ? rule.isEnabled.inherited : currentRule.isEnabled,
                        severity: checked === true ? rule.severity.inherited : currentRule.severity,
                        params:
                          checked === true
                            ? rule.params.reduce<Record<string, MonitoringRuleParamValue>>(
                                (result, param) => {
                                  result[param.key] = param.inherited;
                                  return result;
                                },
                                {}
                              )
                            : currentRule.params,
                      }))
                    }
                  />
                  <Label htmlFor={`${rule.ruleCode}-inherit`}>Arv fra sensortype</Label>
                </div>
              </div>

              <div className="rounded-md bg-muted/40 p-3 text-sm text-muted-foreground space-y-1">
                <div className="font-medium text-foreground">Arvet standard</div>
                <div>Aktiv: {rule.isEnabled.inherited ? 'Ja' : 'Nej'}</div>
                <div>Severity: {rule.severity.inherited}</div>
                {rule.params.map((param) => (
                  <div key={param.key}>
                    {param.label}: {formatValue(param.inherited)}
                  </div>
                ))}
              </div>

              {!draftRule.isInherited && (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor={`${rule.ruleCode}-enabled`}>Aktiv</Label>
                      <div className="flex h-9 items-center">
                        <Switch
                          id={`${rule.ruleCode}-enabled`}
                          checked={draftRule.isEnabled}
                          onCheckedChange={(checked) =>
                            updateRule(rule.ruleCode, (currentRule) => ({
                              ...currentRule,
                              isEnabled: checked,
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Severity</Label>
                      <Select
                        value={draftRule.severity}
                        onValueChange={(value) =>
                          updateRule(rule.ruleCode, (currentRule) => ({
                            ...currentRule,
                            severity:
                              value as MonitoringDeviceRuleDraft['rules'][number]['severity'],
                          }))
                        }
                      >
                        <SelectTrigger aria-label={`${rule.ruleCode} device severity`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="critical">critical</SelectItem>
                          <SelectItem value="warning">warning</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {editableParams.map((param) => (
                      <div key={param.key} className="space-y-2">
                        <Label htmlFor={`${rule.ruleCode}-${param.key}`}>{param.label}</Label>
                        <Input
                          id={`${rule.ruleCode}-${param.key}`}
                          type={param.type === 'number' ? 'number' : 'text'}
                          min={param.min}
                          max={param.max}
                          step={param.step}
                          value={String(draftRule.params[param.key] ?? '')}
                          onChange={(event) =>
                            updateRule(rule.ruleCode, (currentRule) => ({
                              ...currentRule,
                              params: {
                                ...currentRule.params,
                                [param.key]:
                                  param.type === 'number' ? event.target.value : event.target.value,
                              },
                            }))
                          }
                        />
                        <div className="text-xs text-muted-foreground">{param.description}</div>
                      </div>
                    ))}
                  </div>

                  {readOnlyParams.length > 0 && (
                    <div className="rounded-md bg-muted/40 p-3 text-sm text-muted-foreground space-y-1">
                      {readOnlyParams.map((param) => (
                        <div key={param.key}>
                          <span className="font-medium text-foreground">{param.label}:</span>{' '}
                          {param.description}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() =>
                        updateRule(rule.ruleCode, () => ({
                          ruleCode: rule.ruleCode,
                          isInherited: true,
                          isEnabled: rule.isEnabled.inherited,
                          severity: rule.severity.inherited,
                          params: rule.params.reduce<Record<string, MonitoringRuleParamValue>>(
                            (result, param) => {
                              result[param.key] = param.inherited;
                              return result;
                            },
                            {}
                          ),
                        }))
                      }
                    >
                      Nulstil til standard
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}
